import { useCallback, useMemo } from 'react';

import { AgGridServerTable } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams, ISelectionActionProps, ITableActionProps } from 'core/types';
import { discountService } from 'corporate/services';
import {
  RemovePricingProfileRulesButton,
  UpsertPricingProfileRuleButton,
} from 'pricing/components';
import { PricingProfileRuleType } from 'pricing/enums';
import { IPricingProfile, IPricingProfileRule } from 'pricing/types';

import { getColumnDef } from './columns';

interface IProps {
  pricingProfile: IPricingProfile;
  isReadOnly?: boolean;
}

export const RuleList = ({ pricingProfile, isReadOnly = false }: IProps) => {
  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
    }),
    []
  );

  const columnDefs = useMemo(
    () => getColumnDef({ pricingProfile, isEditable: !isReadOnly }),
    [pricingProfile, isReadOnly]
  );

  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <UpsertPricingProfileRuleButton
          key="addMarginRule"
          pricingProfile={pricingProfile}
          type={PricingProfileRuleType.MARGIN}
          onSubmitSuccess={() => gridApi?.refreshServerSide()}
        />,
        <UpsertPricingProfileRuleButton
          key="addDiscountRule"
          pricingProfile={pricingProfile}
          type={PricingProfileRuleType.DISCOUNT}
          onSubmitSuccess={() => gridApi?.refreshServerSide()}
        />,
      ];
    },
    [isReadOnly, pricingProfile]
  );

  const selectionActionButtons = useCallback(
    ({ gridApi, selectedRows, deselectAllRows }: ISelectionActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <RemovePricingProfileRulesButton
          key="removeSelectedRules"
          pricingProfileRules={selectedRows as IPricingProfileRule[]}
          onRemoveSuccess={() => {
            deselectAllRows();
            gridApi?.refreshServerSide();
          }}
        />,
      ];
    },
    [isReadOnly]
  );

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      return discountService.getDiscounts<IPricingProfileRule>({
        filters: {
          ...filters,
          pricingProfileId: pricingProfile.id,
          structureId: [pricingProfile.discountStructureId, pricingProfile.marginStructureId],
        },
        ...rest,
      });
    },
    [pricingProfile.id, pricingProfile.discountStructureId, pricingProfile.marginStructureId]
  );

  return (
    <SelectionCountProvider>
      <AgGridServerTableProvider
        tableId="pricingProfileRulesTable"
        entityType="Rule"
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        getRows={getRows}
        hasLayoutActions
        hasManageFilters
        isRowSelectable={() => !isReadOnly}
        layoutType={TableLayoutType.PRICING_PROFILE_RULE}
      >
        <AgGridServerTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
        />
      </AgGridServerTableProvider>
    </SelectionCountProvider>
  );
};
